import { LoaderCircleIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import useSWR from 'swr'

import { StatusBadge } from '@/components/ds'
import { Loading } from '@/components/Loading'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SettingsPage, SettingsSection } from '@/containers/Settings/SettingsLayout'
import { apiFetcher } from '@/data/fetcher'
import API, { API_URL } from '@/lib/api'

type SyncEntry = 'bankAccounts' | 'contacts' | 'invoices' | 'expenses'
type SyncState = 'not_synced' | 'synced' | 'synced_partially'

const badgeTone: Record<SyncState, string> = {
  not_synced: 'draft',
  synced_partially: 'pending',
  synced: 'paid',
}

export default function SettingsFakturoidImportPage() {
  const { data, isLoading, mutate } = useSWR<{
    status: 'configured' | 'not-configured' | 'synced'
    message: string
    accounts: string[]
    syncStatus: Record<SyncEntry, SyncState>
  }>(`/api/v2/fakturoid`, apiFetcher)
  const [account, setAccount] = useState<string | undefined>()
  const [syncing, setSyncing] = useState(false)

  const { t } = useTranslation(undefined, {
    keyPrefix: 'settings',
  })

  const sync = () => {
    setSyncing(true)

    API.post(API_URL + '/api/v2/fakturoid/sync', {
      account,
    })
      .catch(() => {
        toast.error('Synchronizace se nepodařila. Zkuste to prosím znovu.')
      })
      .finally(() => {
        setSyncing(false)
        mutate()
      })
  }

  return (
    <SettingsPage
      title="Import z Fakturoidu"
      description="Převezme bankovní účty, kontakty, faktury a náklady z vašeho účtu ve Fakturoidu."
      breadcrumbHref="/settings/fakturoid-import"
    >
      <div className="mb-6 rounded-lg bg-warning-bg px-4 py-3 text-sm text-warning">
        Import udělejte před vytvořením první faktury nebo nákladu. Jinak se mohou střetnout číselné řady a takové
        doklady další import zablokují.
      </div>

      {isLoading && <Loading />}

      {data?.status === 'not-configured' && (
        <SettingsSection
          title="Připojení"
          description="Přesměrujeme vás do Fakturoidu, kde přístup potvrdíte."
        >
          <Button asChild>
            <a href={API_URL + '/api/v2/fakturoid/init'}>{t('fakturoidImport.connectButton')}</a>
          </Button>
        </SettingsSection>
      )}

      {data?.status === 'synced' && (
        <SettingsSection title="Hotovo">
          <p className="text-sm text-muted-foreground">{t('fakturoidImport.synced')}</p>
        </SettingsSection>
      )}

      {data?.status === 'configured' && (
        <>
          <SettingsSection title={t('fakturoidImport.syncStatus.title')}>
            <ul className="divide-y overflow-hidden rounded-lg border">
              {Object.entries(data.syncStatus).map(([entry, status]) => (
                <li key={entry} className="flex min-h-11 items-center justify-between gap-4 px-4 text-sm">
                  <span>{t(`fakturoidImport.syncStatus.entries.${entry as SyncEntry}`)}</span>
                  <StatusBadge
                    status={badgeTone[status]}
                    label={t(`fakturoidImport.syncStatus.status.${status}`)}
                  />
                </li>
              ))}
            </ul>
          </SettingsSection>

          <SettingsSection
            title="Synchronizace"
            description="Vyberte účet ve Fakturoidu, ze kterého se mají data stáhnout."
          >
            <div className="flex flex-col gap-2 sm:flex-row">
              <Select name="account" onValueChange={(value) => setAccount(value)}>
                <SelectTrigger className="sm:flex-1">
                  <SelectValue placeholder="Vyberte Fakturoid účet" />
                </SelectTrigger>

                <SelectContent>
                  {data.accounts.map((item: string) => (
                    <SelectItem value={item} key={item}>
                      {item}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button onClick={sync} disabled={syncing}>
                {syncing && <LoaderCircleIcon className="animate-spin" />}
                {syncing ? 'Synchronizuji' : t('fakturoidImport.syncButton')}
              </Button>
            </div>
          </SettingsSection>
        </>
      )}
    </SettingsPage>
  )
}
